import * as React from 'react';
import { jsx, css } from '@emotion/react';
import { setTime, wait } from 'Src/me/pages/guest/proposals/new/utils';
import { mediaQueries, autoResponsiveClamp } from 'Src/me/pages/guest/proposals/new/React/viewHelpers';

const { tablet, mobile } = mediaQueries();

type ModalPropsType = {
  children: React.ReactNode;
}

type useModalViewPropsType = {
  keyName: string;
  isDisableOverlay?: boolean;
  isDisableEscKey?: boolean;
}

export type useModalViewReturnType = {
  dialogModalRef: React.RefObject<HTMLDialogElement>;
  openModalHandler: () => void;
  closeModalHandler: () => void;
  removeDisableEscKeyEvent: () => void;
  Modal: React.FC<ModalPropsType>;
}

export const useModalView = ({
  keyName,
  isDisableOverlay = false,
  isDisableEscKey = false,
}: useModalViewPropsType): useModalViewReturnType => {
  const dialogModalRef = React.useRef<HTMLDialogElement>(null);

  // Escキーを無効化する処理と解除する処理
  const disableEscKey = React.useCallback((event: KeyboardEvent) => {
    if (isDisableEscKey && event.key === 'Escape') {
      event.preventDefault();
    }
  }, [isDisableEscKey]);
  const removeDisableEscKeyEvent = React.useCallback(() => {
    document.removeEventListener('keydown', disableEscKey);
  }, [disableEscKey]);

  // モーダルを開く処理
  const openModalHandler = React.useCallback(() => {
    if (!dialogModalRef.current) return;

    dialogModalRef.current.showModal();
    dialogModalRef.current.setAttribute('data-activate', 'true');
  }, []);

  // モーダルを閉じる処理
  const closeModalHandler = React.useCallback(async () => {
    removeDisableEscKeyEvent();

    if (!dialogModalRef.current) return;

    dialogModalRef.current.setAttribute('data-activate', 'false');
    await wait(0.3);
    dialogModalRef.current.close();
  }, [removeDisableEscKeyEvent]);

  const overlayClickHandler = React.useCallback((event: React.MouseEvent<HTMLDialogElement>) => {
    if (!isDisableOverlay && event.target === event.currentTarget) {
      closeModalHandler();
    }
  }, [isDisableOverlay, closeModalHandler]);

  // Escキーを無効化する処理
  React.useEffect(() => {
    document.addEventListener('keydown', disableEscKey);

    return () => {
      removeDisableEscKeyEvent();
    };
  }, [isDisableEscKey, disableEscKey, removeDisableEscKeyEvent]);

  return {
    dialogModalRef,
    openModalHandler,
    closeModalHandler,
    removeDisableEscKeyEvent,
    Modal: ({ children }: ModalPropsType) => (
      <dialog
        id={`${keyName}-modal`}
        ref={dialogModalRef}
        onClick={overlayClickHandler}
        css={css`
          width: fit-content;
          margin: auto;
          overflow: visible;
          inset: 0;
          transition: opacity ${setTime(0.3)}s, scale ${setTime(0.3)}s;

          &::backdrop {
            background: linear-gradient(to bottom, rgb(0 0 0 / 0.8) 60%, rgb(0 0 0 / 0.4) 90%);
            transition: opacity ${setTime(0.3)}s;
          }

          &:not([data-activate="true"]) {
            opacity: 0;
            scale: 0.95;

            &::backdrop {
              opacity: 0;
            }
          }
        `}
      >
        {children}
      </dialog>
    ),
  };
};
